import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { registerUser } from '../utils/api/userAPI';
import { getAllOrganisationData } from '../utils/api/organisationAPI';
import { Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const AddUserLayer = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    roleId: 'c18df413-3598-469f-b0bb-7bc341e0f4ef',
    organizationId: '',
  });

  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getAllOrganisationData({ setIsLoading })
      .then(data => setOrganizations(data.organizations || []))
      .catch(err => toast.error("Failed to fetch organizations"));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await registerUser(formData);
      toast.success(res.message);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        roleId: 'c18df413-3598-469f-b0bb-7bc341e0f4ef',
        organizationId: '',
      });
    } catch (err) {
      toast.error(typeof err === 'string' ? err : err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const renderInput = (name, type, placeholder, icon, label, required = true) => (
    <div className="col-md-6">
      <label className="form-label" style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        <Icon icon={icon} width="18" height="18" style={iconStyle} />
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
          className="form-control"
          placeholder={placeholder}
          style={inputStyle}
          required={required}
        />
      </div>
    </div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="col-lg-12"
    >
      <div className="card" style={cardStyle}>
        <div className="card-header" style={cardHeaderStyle}>
          <h5 className="card-title mb-0" style={titleStyle}>
            <Icon icon="lucide:user-plus" className="me-2" width="22" height="22" />
            Add User Form
          </h5>
        </div>
        <div className="card-body" style={cardBodyStyle}>
          <form className="row gy-4" onSubmit={handleSubmit}>
            {renderInput('firstName', 'text', 'Enter first name', 'lucide:user', 'First Name')}
            {renderInput('lastName', 'text', 'Enter last name', 'lucide:user', 'Last Name')}
            {renderInput('email', 'email', 'Enter email', 'lucide:mail', 'Email')}
            {renderInput('phone', 'text', 'Enter phone', 'lucide:phone', 'Phone')}
            {renderInput('password', 'password', 'Enter password', 'lucide:lock', 'Password')}

            <div className="col-md-6">
              <label className="form-label" style={labelStyle}>Organization</label>
              <div className="position-relative">
                <Icon icon="lucide:building" width="18" height="18" style={iconStyle} />
                <select
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                  className="form-control"
                  style={selectStyle}
                  required
                >
                  <option value="">Select Organization</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" width="18" height="18" style={dropdownIconStyle} />
              </div>
            </div>

            <div className="col-12" style={footerStyle}>
              <button
                type="submit"
                className="btn btn-primary-500"
                disabled={isLoading}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
};

// Styles (unchanged)
const labelStyle = {
  fontWeight: '500',
  marginBottom: '8px',
  color: '#344767',
  fontSize: '14px'
};
const iconStyle = {
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#6c757d'
};
const inputStyle = {
  paddingLeft: '40px',
  height: '45px',
  borderColor: '#e1e5eb',
  fontSize: '14px'
};
const selectStyle = {
  paddingLeft: '40px',
  height: '45px',
  appearance: 'none',
  borderColor: '#e1e5eb',
  fontSize: '14px'
};
const dropdownIconStyle = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: '#6c757d'
};
const footerStyle = {
  background: '#ffffff',
  padding: '20px 25px',
  borderTop: '1px solid #eaeaea',
  borderBottomLeftRadius: '10px',
  borderBottomRightRadius: '10px',
  display: 'flex',
  justifyContent: 'flex-end'
};
const cardStyle = {
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  border: 'none'
};
const cardHeaderStyle = {
  background: '#f8f9fa',
  borderBottom: '1px solid #eaeaea',
  padding: '20px 25px'
};
const cardBodyStyle = {
  padding: '30px 25px',
  background: '#ffffff'
};
const titleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#344767'
};

export default AddUserLayer;
